import {HTTP_STATUS_CODE} from 'definitions/constant'
import {unwrapAPIError} from 'utils'
import {getAnalyticsBaseUrl, requestHandler} from '../HTTP'
import {HttpMethods, EndPoints} from '../HTTP/HTTP.types'
import {
  MCPUsageSummaryAPIResponse,
  MCPUsageServersAPIParams,
  MCPUsageServersAPIResponseData,
  MCPUsageServerUsersAPIResponseData,
  MCPUsageUsersAPIParams,
  MCPUsageUsersAPIResponseData,
  MCPUsageUserServersAPIResponseData,
  MCPUsageExportResponse,
  MCPUsageExportResult,
} from 'interfaces'
import Logger from 'services/Logger'

const analyticsBaseUrl = getAnalyticsBaseUrl()

/** MCP usage summary for the 7-day window. Schema: GET /mcp-usage/summary */
const getMCPUsageSummary = async (): Promise<MCPUsageSummaryAPIResponse> => {
  try {
    const {data: responseData} =
      await requestHandler<MCPUsageSummaryAPIResponse>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.MCPUsageSummary}`,
      })
    return responseData
  } catch (error) {
    // Axios failures are owned by the interceptor (LogAPIError). Keep local
    // non-Axios failures (e.g. encodeURIComponent URIError) visible in Sentry.
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** Top MCP servers, paged. Schema: GET /mcp-usage/servers */
const getMCPUsageServers = async (
  params?: MCPUsageServersAPIParams,
): Promise<MCPUsageServersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<MCPUsageServersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.MCPUsageServers}`,
        options: {
          params: {
            ...params,
          },
        },
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** Top 50 users of an MCP server. Schema: GET /mcp-usage/servers/users?serverName= */
const getMCPUsageServerUsers = async (
  serverName: string,
): Promise<MCPUsageServerUsersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<MCPUsageServerUsersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.MCPUsageServerUsers}`,
        options: {
          params: {serverName},
          // Axios encodes spaces as `+` by default; keep `%20` for API Gateway.
          paramsSerializer: {encode: encodeURIComponent},
        },
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** Top MCP users, paged. Schema: GET /mcp-usage/users */
const getMCPUsageUsers = async (
  params?: MCPUsageUsersAPIParams,
): Promise<MCPUsageUsersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<MCPUsageUsersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.MCPUsageUsers}`,
        options: {
          params: {
            ...params,
          },
        },
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** MCP servers a user accessed. Schema: GET /mcp-usage/users/{email}/servers */
const getMCPUsageUserServers = async (
  email: string,
): Promise<MCPUsageUserServersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<MCPUsageUserServersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.MCPUsageUserServers.replace(
          '{email}',
          encodeURIComponent(email),
        )}`,
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** CSV of every user's MCP server usage. Schema: GET /mcp-usage/export */
const getMCPUsageExport = async (): Promise<MCPUsageExportResult> => {
  try {
    const response = await requestHandler<MCPUsageExportResponse>({
      method: HttpMethods.GET,
      url: `${analyticsBaseUrl}${EndPoints.MCPUsageExport}`,
    })

    if (response.status === HTTP_STATUS_CODE.NO_CONTENT) {
      return {isEmpty: true}
    }

    return {
      fileUrl: response.data?.data?.fileUrl,
      isEmpty: false,
    }
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

export const MCPUsageService = {
  getMCPUsageSummary,
  getMCPUsageServers,
  getMCPUsageServerUsers,
  getMCPUsageUsers,
  getMCPUsageUserServers,
  getMCPUsageExport,
}
